import React, { useState, useRef, useEffect } from 'react';
import { useLongPress } from '@uidotdev/usehooks';
import { useLanguage } from '../../contexts/LanguageContext';

interface ShiftSelectionModalProps {
  onSelect: (shift: 'zmiana_1' | 'zmiana_2') => void;
}

const ShiftSelectionModal: React.FC<ShiftSelectionModalProps> = ({ onSelect }) => {
  const { t } = useLanguage();
  const [pressingShift1, setPressingShift1] = useState(false);
  const [pressingShift2, setPressingShift2] = useState(false);
  const [progressShift1, setProgressShift1] = useState(0);
  const [progressShift2, setProgressShift2] = useState(0);
  const intervalRef1 = useRef<NodeJS.Timeout | null>(null);
  const intervalRef2 = useRef<NodeJS.Timeout | null>(null);
  const startTimeRef1 = useRef<number | null>(null);
  const startTimeRef2 = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (intervalRef1.current) clearInterval(intervalRef1.current);
      if (intervalRef2.current) clearInterval(intervalRef2.current);
    };
  }, []);

  const handleLongPressShift1 = () => {
    onSelect('zmiana_1');
  };

  const handleLongPressShift2 = () => {
    onSelect('zmiana_2');
  };

  const longPressShift1 = useLongPress(
    handleLongPressShift1,
    {
      threshold: 3000, // 3 sekundy
      onStart: () => {
        setPressingShift1(true);
        setProgressShift1(0);
        startTimeRef1.current = Date.now();
        // Animate progress
        intervalRef1.current = setInterval(() => {
          if (startTimeRef1.current) {
            const elapsed = Date.now() - startTimeRef1.current;
            const progress = Math.min((elapsed / 3000) * 100, 100);
            setProgressShift1(progress);
            if (progress >= 100 && intervalRef1.current) {
              clearInterval(intervalRef1.current);
            }
          }
        }, 50);
      },
      onFinish: () => {
        setPressingShift1(false);
        setProgressShift1(0);
        if (intervalRef1.current) {
          clearInterval(intervalRef1.current);
          intervalRef1.current = null;
        }
        startTimeRef1.current = null;
      },
      onCancel: () => {
        setPressingShift1(false);
        setProgressShift1(0);
        if (intervalRef1.current) {
          clearInterval(intervalRef1.current);
          intervalRef1.current = null;
        }
        startTimeRef1.current = null;
      },
    }
  );

  const longPressShift2 = useLongPress(
    handleLongPressShift2,
    {
      threshold: 3000, // 3 sekundy
      onStart: () => {
        setPressingShift2(true);
        setProgressShift2(0);
        startTimeRef2.current = Date.now();
        // Animate progress
        intervalRef2.current = setInterval(() => {
          if (startTimeRef2.current) {
            const elapsed = Date.now() - startTimeRef2.current;
            const progress = Math.min((elapsed / 3000) * 100, 100);
            setProgressShift2(progress);
            if (progress >= 100 && intervalRef2.current) {
              clearInterval(intervalRef2.current);
            }
          }
        }, 50);
      },
      onFinish: () => {
        setPressingShift2(false);
        setProgressShift2(0);
        if (intervalRef2.current) {
          clearInterval(intervalRef2.current);
          intervalRef2.current = null;
        }
        startTimeRef2.current = null;
      },
      onCancel: () => {
        setPressingShift2(false);
        setProgressShift2(0);
        if (intervalRef2.current) {
          clearInterval(intervalRef2.current);
          intervalRef2.current = null;
        }
        startTimeRef2.current = null;
      },
    }
  );

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000,
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '16px',
          maxWidth: '500px',
          width: '90%',
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          textAlign: 'center',
        }}
      >
        <h2 style={{ marginTop: 0, marginBottom: '30px', color: '#667eea', fontSize: '24px' }}>
          {t('shift.selectShift')}
        </h2>
        <p style={{ marginBottom: '30px', color: '#4a5568', fontSize: '16px' }}>
          {t('shift.holdToSelect')}
        </p>

        <div style={{ display: 'flex', gap: '20px', justifyContent: 'center' }}>
          {/* Zmiana 1 */}
          <div style={{ flex: 1 }}>
            <button
              {...longPressShift1}
              style={{
                width: '100%',
                padding: '30px 20px',
                fontSize: '20px',
                fontWeight: '600',
                backgroundColor: pressingShift1 ? '#667eea' : '#f0f0f0',
                color: pressingShift1 ? 'white' : '#4a5568',
                border: pressingShift1 ? '3px solid #667eea' : '3px solid #e1e8ed',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.1s ease',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div style={{ position: 'relative', zIndex: 2 }}>
                {t('shift.shift1')}
              </div>
              {pressingShift1 && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    height: `${progressShift1}%`,
                    width: '100%',
                    backgroundColor: 'rgba(102, 126, 234, 0.3)',
                    transition: 'height 0.1s linear',
                  }}
                />
              )}
            </button>
            {pressingShift1 && (
              <div style={{ marginTop: '10px', fontSize: '14px', color: '#667eea', fontWeight: '500' }}>
                {t('shift.holdProgress', { progress: Math.round(progressShift1).toString() })}
              </div>
            )}
          </div>

          {/* Zmiana 2 */}
          <div style={{ flex: 1 }}>
            <button
              {...longPressShift2}
              style={{
                width: '100%',
                padding: '30px 20px',
                fontSize: '20px',
                fontWeight: '600',
                backgroundColor: pressingShift2 ? '#667eea' : '#f0f0f0',
                color: pressingShift2 ? 'white' : '#4a5568',
                border: pressingShift2 ? '3px solid #667eea' : '3px solid #e1e8ed',
                borderRadius: '12px',
                cursor: 'pointer',
                transition: 'all 0.1s ease',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <div style={{ position: 'relative', zIndex: 2 }}>
                {t('shift.shift2')}
              </div>
              {pressingShift2 && (
                <div
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    height: `${progressShift2}%`,
                    width: '100%',
                    backgroundColor: 'rgba(102, 126, 234, 0.3)',
                    transition: 'height 0.1s linear',
                  }}
                />
              )}
            </button>
            {pressingShift2 && (
              <div style={{ marginTop: '10px', fontSize: '14px', color: '#667eea', fontWeight: '500' }}>
                {t('shift.holdProgress', { progress: Math.round(progressShift2).toString() })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShiftSelectionModal;

